import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { alpha } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';

import AppNavbar from '../components/AppNavbar';
import Header from '../components/Header';
import SideMenu from '../components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import { setPartnerProfile } from '../redux/authSlice';
import axiosInstance from '../utils/axiosInstance';
import {
  getAccountTypeLabel,
  getAccountPartnerMembership,
  getBusinessRoleLabel,
  getCurrentAccountRoleLabel,
  getInitials,
  getMembershipDisplayName,
  getUserDisplayName,
  getUserEmail,
} from '../utils/userProfile';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

function formatDateTime(value) {
  if (!value) {
    return 'Not available';
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function formatLabel(value) {
  return String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase()) || 'Not available';
}

function statusColor(status) {
  const normalized = String(status || '').toLowerCase();

  if (normalized === 'active') {
    return 'success';
  }
  if (normalized === 'invited' || normalized === 'pending') {
    return 'warning';
  }
  if (normalized === 'inactive' || normalized === 'disabled') {
    return 'default';
  }

  return 'primary';
}

function DetailItem({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box
        sx={(theme) => ({
          width: 36,
          height: 36,
          borderRadius: 1,
          display: 'grid',
          placeItems: 'center',
          color: 'primary.main',
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          flexShrink: 0,
        })}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 700 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.25, overflowWrap: 'anywhere' }}>
          {value || 'Not available'}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function MyAccount(props) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const partnerProfile = useSelector((state) => state.auth.partnerProfile);

  const [profile, setProfile] = useState(partnerProfile);
  const [loading, setLoading] = useState(!partnerProfile);
  const [error, setError] = useState('');
  const [emailPreferences, setEmailPreferences] = useState(null);
  const [emailPreferencesLoading, setEmailPreferencesLoading] = useState(true);
  const [emailPreferencesError, setEmailPreferencesError] = useState('');
  const [emailPreferencesSaving, setEmailPreferencesSaving] = useState({});
  const [emailPreferencesSaved, setEmailPreferencesSaved] = useState(false);

  const activeProfile = profile || partnerProfile;
  const displayName = getUserDisplayName(user, activeProfile);
  const email = getUserEmail(user, activeProfile);

  const accountMembership = useMemo(() => {
    return getAccountPartnerMembership(activeProfile);
  }, [activeProfile]);

  const accountTypeLabel = getAccountTypeLabel(user, activeProfile);
  const accountRoleLabel = getCurrentAccountRoleLabel(user, activeProfile, accountMembership);
  const accountStatusLabel = formatLabel(activeProfile?.status);
  const accountPartnerLabel = accountMembership
    ? getMembershipDisplayName(accountMembership)
    : 'No associated partner or business unit';

  const emailPreferenceGroups = useMemo(() => {
    const groups = [];
    for (const preference of emailPreferences?.available_preferences || []) {
      let group = groups.find((candidate) => candidate.label === preference.group);
      if (!group) {
        group = { label: preference.group, preferences: [] };
        groups.push(group);
      }
      group.preferences.push(preference);
    }
    return groups;
  }, [emailPreferences]);

  const loadProfile = React.useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axiosInstance.get('/account/me');
      setProfile(response.data);
      dispatch(setPartnerProfile(response.data));
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Unable to load account details right now.');
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  const loadEmailPreferences = React.useCallback(async () => {
    setEmailPreferencesLoading(true);
    setEmailPreferencesError('');

    try {
      const response = await axiosInstance.get('/account/notification-preferences');
      setEmailPreferences(response.data);
    } catch (requestError) {
      setEmailPreferencesError(
        requestError?.response?.data?.message || 'Unable to load email notification preferences right now.',
      );
    } finally {
      setEmailPreferencesLoading(false);
    }
  }, []);

  const handleEmailPreferenceChange = React.useCallback(async (key, checked) => {
    const previousValue = Boolean(emailPreferences?.preferences?.[key]);
    setEmailPreferences((current) => ({
      ...current,
      preferences: {
        ...current?.preferences,
        [key]: checked,
      },
    }));
    setEmailPreferencesSaving((current) => ({ ...current, [key]: true }));
    setEmailPreferencesSaved(false);
    setEmailPreferencesError('');

    try {
      const response = await axiosInstance.patch('/account/notification-preferences', {
        preferences: { [key]: checked },
      });
      setEmailPreferences(response.data);
      setEmailPreferencesSaved(true);
    } catch (requestError) {
      setEmailPreferences((current) => ({
        ...current,
        preferences: {
          ...current?.preferences,
          [key]: previousValue,
        },
      }));
      setEmailPreferencesError(
        requestError?.response?.data?.message || 'Unable to save this email notification preference.',
      );
    } finally {
      setEmailPreferencesSaving((current) => ({ ...current, [key]: false }));
    }
  }, [emailPreferences]);

  useEffect(() => {
    if (partnerProfile) {
      setProfile(partnerProfile);
      setLoading(false);
      return;
    }

    loadProfile();
  }, [loadProfile, partnerProfile]);

  useEffect(() => {
    loadEmailPreferences();
  }, [loadEmailPreferences]);

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />

        <Box component="main" sx={{ flexGrow: 1, p: 3, minWidth: 0 }}>
          <Header
            breadcrumbItems={[
              { label: 'AMX xConsole', href: '/' },
              { label: 'My Account' },
            ]}
          />

          <Stack spacing={3}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, md: 3 },
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
                  <Avatar
                    sx={(theme) => ({
                      width: 64,
                      height: 64,
                      bgcolor: alpha(theme.palette.primary.main, 0.14),
                      color: 'primary.main',
                      fontWeight: 700,
                    })}
                  >
                    {getInitials(displayName)}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                      <Typography variant="h4" sx={{ fontWeight: 800, overflowWrap: 'anywhere' }}>
                        {displayName}
                      </Typography>
                      {activeProfile?.status ? (
                        <Chip
                          size="small"
                          color={statusColor(activeProfile.status)}
                          label={formatLabel(activeProfile.status)}
                        />
                      ) : null}
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
                      {email || 'Email not available'}
                    </Typography>
                  </Box>
                </Stack>

                <Button
                  variant="outlined"
                  startIcon={loading ? <CircularProgress size={16} /> : <RefreshRoundedIcon />}
                  onClick={loadProfile}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Stack>
            </Paper>

            {error ? <Alert severity="error">{error}</Alert> : null}

            {loading && !activeProfile ? (
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                  <CircularProgress size={24} />
                  <Typography>Loading account details...</Typography>
                </Stack>
              </Paper>
            ) : (
              <>
                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="stretch">
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      flex: 1,
                    }}
                  >
                    <Stack spacing={2.5}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Account Details
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Identity information returned by AMX Core.
                        </Typography>
                      </Box>
                      <Divider />
                      <DetailItem icon={<BadgeRoundedIcon fontSize="small" />} label="Full name" value={activeProfile?.full_name || displayName} />
                      <DetailItem icon={<EmailRoundedIcon fontSize="small" />} label="Email" value={email} />
                      <DetailItem icon={<AccountCircleRoundedIcon fontSize="small" />} label="Account ID" value={activeProfile?.cognito_sub || user?.sub} />
                    </Stack>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      flex: 1,
                    }}
                  >
                    <Stack spacing={2.5}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Access
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Current application roles and account lifecycle.
                        </Typography>
                      </Box>
                      <Divider />
                      <DetailItem
                        icon={<BadgeRoundedIcon fontSize="small" />}
                        label="Account type"
                        value={accountTypeLabel}
                      />
                      <DetailItem
                        icon={<VerifiedUserRoundedIcon fontSize="small" />}
                        label="Account role"
                        value={accountRoleLabel}
                      />
                      <DetailItem
                        icon={<BusinessRoundedIcon fontSize="small" />}
                        label="Partner / Business Unit"
                        value={accountPartnerLabel}
                      />
                      <DetailItem
                        icon={<CalendarMonthRoundedIcon fontSize="small" />}
                        label="Account status"
                        value={accountStatusLabel}
                      />
                    </Stack>
                  </Paper>
                </Stack>

                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Associated Partner/Business Unit
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        The organization context linked to this AMX account.
                      </Typography>
                    </Box>
                    {accountMembership?.role ? (
                      <Chip label={getBusinessRoleLabel(accountMembership.role)} variant="outlined" />
                    ) : null}
                  </Stack>

                  <Divider sx={{ my: 2.5 }} />

                  {accountMembership ? (
                    <Box
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                      }}
                    >
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between">
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {getMembershipDisplayName(accountMembership)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {[accountMembership.partner_code, accountMembership.partner_id].filter(Boolean).join(' • ')}
                          </Typography>
                        </Box>

                        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                          {accountMembership.partner_type ? (
                            <Chip size="small" label={formatLabel(accountMembership.partner_type)} variant="outlined" />
                          ) : null}
                          <Chip size="small" label={getBusinessRoleLabel(accountMembership.role)} variant="outlined" />
                          <Chip size="small" label={`Joined ${formatDateTime(accountMembership.joined_at)}`} variant="outlined" />
                        </Stack>
                      </Stack>
                    </Box>
                  ) : (
                    <Alert severity="info">No associated partner or business unit is linked to this account.</Alert>
                  )}
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Email Notifications
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Control which AMX events are also sent to your email. In-app notifications and access are unchanged.
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2.5 }} />

                  {emailPreferencesError ? <Alert severity="error" sx={{ mb: 2 }}>{emailPreferencesError}</Alert> : null}
                  {emailPreferencesSaved ? <Alert severity="success" sx={{ mb: 2 }}>Email notification preference saved.</Alert> : null}

                  {emailPreferencesLoading ? (
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <CircularProgress size={20} />
                      <Typography variant="body2">Loading email notification preferences...</Typography>
                    </Stack>
                  ) : emailPreferenceGroups.length ? (
                    <Stack spacing={3}>
                      {emailPreferenceGroups.map((group, groupIndex) => (
                        <Box key={group.label}>
                          {groupIndex > 0 ? <Divider sx={{ mb: 2.5 }} /> : null}
                          {groupIndex > 0 ? (
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                              {group.label}
                            </Typography>
                          ) : null}
                          <Stack spacing={1.5}>
                            {group.preferences.map((preference) => (
                              <Stack
                                key={preference.key}
                                direction="row"
                                spacing={2}
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{
                                  p: 1.5,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                }}
                              >
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                    {preference.label}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {preference.description}
                                  </Typography>
                                </Box>
                                <Switch
                                  checked={Boolean(emailPreferences?.preferences?.[preference.key])}
                                  onChange={(event) => handleEmailPreferenceChange(preference.key, event.target.checked)}
                                  disabled={Boolean(emailPreferencesSaving[preference.key])}
                                  inputProps={{ 'aria-label': preference.label }}
                                />
                              </Stack>
                            ))}
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Alert severity="info">No email notification preferences are available for this account.</Alert>
                  )}
                </Paper>
              </>
            )}
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}
